import React from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Error Boundary que captura erros de carregamento de chunks (React.lazy)
 * e outros erros de renderizacao.
 *
 * Comportamento:
 * - Se for erro de chunk (apos deploy, HTML stale + assets novos), faz reload
 *   com CACHE-BUST na URL (?_cb=ts) para forcar o browser a buscar index.html
 *   fresh do servidor — resolve o caso classico de "app quebrou depois do deploy"
 * - Limita 2 retries por sessao para evitar loop infinito
 * - Para erros de renderizacao normais (bug no codigo), mostra botoes manuais
 */
const RETRY_KEY = 'chunk_error_reload';
const RETRY_COUNT_KEY = 'chunk_error_retry_count';
const MAX_RETRIES = 2;
const CACHE_BUST_PARAM = '_cb';

function isChunkError(err) {
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  const m = err.message || '';
  return (
    m.includes('Loading chunk') ||
    m.includes('Loading CSS chunk') ||
    m.includes('Failed to fetch dynamically imported module') ||
    m.includes('Importing a module script failed') ||
    m.includes("error loading dynamically imported module")
  );
}

function reloadWithCacheBust() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(CACHE_BUST_PARAM, String(Date.now()));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isRetrying: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const chunkErr = isChunkError(error);
    const count = parseInt(sessionStorage.getItem(RETRY_COUNT_KEY) || '0', 10);
    const lastRetry = parseInt(sessionStorage.getItem(RETRY_KEY) || '0', 10);
    const now = Date.now();
    const throttled = now - lastRetry < 3000; // 3s entre retries

    if (chunkErr && count < MAX_RETRIES && !throttled) {
      sessionStorage.setItem(RETRY_KEY, String(now));
      sessionStorage.setItem(RETRY_COUNT_KEY, String(count + 1));
      this.setState({ isRetrying: true });
      reloadWithCacheBust();
      return;
    }

    // sucesso de navegacao limpa o contador (outro effect cuidara disso)
    console.error('[ChunkErrorBoundary]', error, errorInfo);
  }

  handleRetry = () => {
    sessionStorage.removeItem(RETRY_KEY);
    sessionStorage.removeItem(RETRY_COUNT_KEY);
    reloadWithCacheBust();
  };

  handleGoBack = () => {
    this.setState({ hasError: false });
    window.history.back();
  };

  render() {
    if (this.state.isRetrying) {
      return (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (this.state.hasError) {
      return (
        <div
          className="flex flex-col items-center justify-center h-64 gap-4"
          data-testid="chunk-error-boundary"
        >
          <p className="text-muted-foreground text-sm">Erro ao carregar esta secao.</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={this.handleRetry}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Atualizar Pagina
            </Button>
            <Button variant="ghost" size="sm" onClick={this.handleGoBack}>
              Voltar
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Reseta contador quando o usuario navega com sucesso (evita travar em 2 retries)
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    // Se chegamos aqui sem o boundary ter disparado, limpa contadores
    setTimeout(() => {
      sessionStorage.removeItem('chunk_error_reload');
      sessionStorage.removeItem('chunk_error_retry_count');
    }, 5000);
  });
}

export default ChunkErrorBoundary;
